import { normalizeAgreementTerms } from '@/types/agreement-terms';
import { normalizeAgreementTermMonths } from '@/lib/constants/agreement-term-months';
import { normalizeAgreementHardwareItemName } from '@/lib/sla/draft-payload';
import type { Quote } from '@/types/quotes';
import type { Invoice } from '@/types/invoices';
import type {
  SlaAgreement,
  SlaAgreementCustomerSubmission,
  SlaAgreementDraftPayload,
  SlaAgreementEvent,
  SlaAgreementRevisionRequest,
  SlaAgreementSendPackage,
  SlaAgreementSigner,
  SlaAgreementSignerToken,
  SlaAgreementReconciliation,
  SlaAgreementWritebackResult,
} from '@/types/sla-agreements';

export const DEFAULT_SLA_TEMPLATE_KEY = 'sla_installation_optional';
export const DEFAULT_SLA_TEMPLATE_VERSION = '2026-04-02';

export function createEmptySlaDraftPayload(): SlaAgreementDraftPayload {
  return {
    source_type: null,
    source_reference: null,
    source_quote_number: null,
    customer_name: null,
    legal_entity_name: null,
    primary_location_name: null,
    primary_location_address: null,
    signer_name: null,
    signer_title: null,
    signer_email: null,
    notice_address: null,
    pos_system: null,
    contract_term_months: 24,
    software_license_fee_amount: null,
    software_license_fee_period: null,
    stores: [],
    hardware_schedule_rows: [],
  };
}

export function normalizeSlaDraftPayload(input?: Partial<SlaAgreementDraftPayload> | null): SlaAgreementDraftPayload {
  return {
    ...createEmptySlaDraftPayload(),
    ...input,
    source_type: input?.source_type ?? null,
    source_reference: input?.source_reference?.trim() || null,
    source_quote_number: input?.source_quote_number?.trim() || null,
    customer_name: input?.customer_name?.trim() || null,
    legal_entity_name: input?.legal_entity_name?.trim() || null,
    primary_location_name: input?.primary_location_name?.trim() || null,
    primary_location_address: input?.primary_location_address?.trim() || null,
    signer_name: input?.signer_name?.trim() || null,
    signer_title: input?.signer_title?.trim() || null,
    signer_email: input?.signer_email?.trim() || null,
    notice_address: input?.notice_address?.trim() || null,
    pos_system: input?.pos_system?.trim() || null,
    contract_term_months: normalizeAgreementTermMonths(input?.contract_term_months),
    software_license_fee_amount:
      input?.software_license_fee_amount === null || input?.software_license_fee_amount === undefined
        ? null
        : Number(input.software_license_fee_amount),
    software_license_fee_period: input?.software_license_fee_period ?? null,
    stores: (input?.stores || []).map((store, index) => ({
      id: store.id || `store-${index + 1}`,
      expected_location_id: store.expected_location_id ?? null,
      name: store.name?.trim() || null,
      address: store.address?.trim() || null,
      tentative_installation_date: store.tentative_installation_date || null,
    })),
    hardware_schedule_rows: (input?.hardware_schedule_rows || []).map((row, index) => ({
      id: row.id || `hardware-${index + 1}`,
      line_number: row.line_number ?? index + 1,
      description: normalizeAgreementHardwareItemName(row.description),
      quantity: Number(row.quantity || 0),
      unit_price: Number(row.unit_price || 0),
      line_total:
        row.line_total === undefined || row.line_total === null
          ? Number(row.quantity || 0) * Number(row.unit_price || 0)
          : Number(row.line_total),
      display_type_id: row.display_type_id ?? null,
    })),
  };
}

export function normalizeSlaReconciliation(
  input?: Partial<SlaAgreementReconciliation> | null
): SlaAgreementReconciliation | null {
  if (!input) {
    return null;
  }

  const normalized: SlaAgreementReconciliation = {
    execution_date: input.execution_date || null,
    legal_entity_name: input.legal_entity_name?.trim() || '',
    pos_system: input.pos_system?.trim() || null,
    signer_name: input.signer_name?.trim() || '',
    signer_title: input.signer_title?.trim() || '',
    signer_email: input.signer_email?.trim() || '',
    notice_address: input.notice_address?.trim() || '',
    customer_initials: input.customer_initials?.trim() || null,
    customer_initial_date: input.customer_initial_date || null,
    stores: (input.stores || []).map((store, index) => ({
      store_id: store.store_id || `store-${index + 1}`,
      store_name: store.store_name?.trim() || '',
      store_address: store.store_address?.trim() || null,
      tentative_installation_date: store.tentative_installation_date || null,
      location_id: store.location_id ?? null,
    })),
  };

  if (
    !normalized.legal_entity_name &&
    !normalized.pos_system &&
    !normalized.signer_name &&
    normalized.stores.length === 0
  ) {
    return null;
  }

  return normalized;
}

export function normalizeSlaAgreementCustomerSubmission(
  input?: Partial<SlaAgreementCustomerSubmission> | null
): SlaAgreementCustomerSubmission | null {
  if (!input) {
    return null;
  }

  return {
    execution_date: input.execution_date || null,
    legal_entity_name: input.legal_entity_name?.trim() || '',
    pos_system: input.pos_system?.trim() || null,
    signer_name: input.signer_name?.trim() || '',
    signer_title: input.signer_title?.trim() || '',
    signer_email: input.signer_email?.trim() || '',
    notice_address: input.notice_address?.trim() || '',
    customer_initials: input.customer_initials?.trim() || null,
    customer_initial_date: input.customer_initial_date || null,
    stores: (input.stores || []).map((store, index) => ({
      store_id: store.store_id || `store-${index + 1}`,
      store_name: store.store_name?.trim() || '',
      store_address: store.store_address?.trim() || null,
      tentative_installation_date: store.tentative_installation_date || null,
      location_id: store.location_id ?? null,
    })),
    signature_type: 'typed',
    signature_text: input.signature_text?.trim() || '',
    consent_accepted: Boolean(input.consent_accepted),
  };
}

export function normalizeSlaWritebackResult(
  input?: Partial<SlaAgreementWritebackResult> | null
): SlaAgreementWritebackResult | null {
  if (!input) {
    return null;
  }

  return {
    location_updates: input.location_updates || [],
    contact_updates: input.contact_updates || [],
    equipment: input.equipment || null,
    warnings: input.warnings || [],
  };
}

export function normalizeQuoteAgreementTerms(quote: Quote): Quote {
  const rawTerms = (quote as Quote & { agreement_terms_json?: unknown }).agreement_terms ?? (quote as Quote & { agreement_terms_json?: unknown }).agreement_terms_json ?? null;
  return {
    ...quote,
    agreement_terms: normalizeAgreementTerms(rawTerms as Parameters<typeof normalizeAgreementTerms>[0]),
  };
}

export function normalizeInvoiceAgreementTerms(invoice: Invoice): Invoice {
  const rawTerms =
    (invoice as Invoice & { agreement_terms_json?: unknown }).agreement_terms ??
    (invoice as Invoice & { agreement_terms_json?: unknown }).agreement_terms_json ??
    null;
  return {
    ...invoice,
    agreement_terms: normalizeAgreementTerms(rawTerms as Parameters<typeof normalizeAgreementTerms>[0]),
  };
}

export function normalizeSlaAgreement(row: Record<string, unknown>): SlaAgreement {
  return {
    ...(row as unknown as SlaAgreement),
    template_key: String(row.template_key || DEFAULT_SLA_TEMPLATE_KEY),
    template_version: String(row.template_version || DEFAULT_SLA_TEMPLATE_VERSION),
    current_send_package_id: typeof row.current_send_package_id === 'string' ? row.current_send_package_id : null,
    draft_payload_json: normalizeSlaDraftPayload((row.draft_payload_json as SlaAgreementDraftPayload | null) || null),
    reconciliation_json: normalizeSlaReconciliation(
      (row.reconciliation_json as SlaAgreementReconciliation | null) || null
    ),
    writeback_result_json: normalizeSlaWritebackResult(
      (row.writeback_result_json as SlaAgreementWritebackResult | null) || null
    ),
    signature_requested_at: typeof row.signature_requested_at === 'string' ? row.signature_requested_at : null,
    signature_request_expires_at:
      typeof row.signature_request_expires_at === 'string' ? row.signature_request_expires_at : null,
    signature_request_revoked_at:
      typeof row.signature_request_revoked_at === 'string' ? row.signature_request_revoked_at : null,
    signature_request_email_id: typeof row.signature_request_email_id === 'string' ? row.signature_request_email_id : null,
    finalized_file_path: typeof row.finalized_file_path === 'string' ? row.finalized_file_path : null,
    finalized_at: typeof row.finalized_at === 'string' ? row.finalized_at : null,
    completed_delivery_at: typeof row.completed_delivery_at === 'string' ? row.completed_delivery_at : null,
    completed_delivery_email_id:
      typeof row.completed_delivery_email_id === 'string' ? row.completed_delivery_email_id : null,
  };
}

function normalizeJsonRecord(input: unknown): Record<string, unknown> | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return null;
  }

  return input as Record<string, unknown>;
}

export function normalizeSlaAgreementSignerToken(row: Record<string, unknown>): SlaAgreementSignerToken {
  return {
    ...(row as unknown as SlaAgreementSignerToken),
    revoked_at: typeof row.revoked_at === 'string' ? row.revoked_at : null,
    expired_at: typeof row.expired_at === 'string' ? row.expired_at : null,
    superseded_at: typeof row.superseded_at === 'string' ? row.superseded_at : null,
    created_by: typeof row.created_by === 'string' ? row.created_by : null,
    updated_by: typeof row.updated_by === 'string' ? row.updated_by : null,
  };
}

export function normalizeSlaAgreementSigner(row: Record<string, unknown>): SlaAgreementSigner {
  const tokens = Array.isArray(row.tokens)
    ? row.tokens.map((token) => normalizeSlaAgreementSignerToken(token as Record<string, unknown>))
    : Array.isArray(row.sla_agreement_signer_tokens)
      ? row.sla_agreement_signer_tokens.map((token) =>
          normalizeSlaAgreementSignerToken(token as Record<string, unknown>)
        )
      : [];

  return {
    ...(row as unknown as SlaAgreementSigner),
    internal_user_id: typeof row.internal_user_id === 'string' ? row.internal_user_id : null,
    reminder_count: Number(row.reminder_count || 0),
    first_reminder_sent_at:
      typeof row.first_reminder_sent_at === 'string' ? row.first_reminder_sent_at : null,
    last_reminder_sent_at:
      typeof row.last_reminder_sent_at === 'string' ? row.last_reminder_sent_at : null,
    name: typeof row.name === 'string' ? row.name : null,
    title: typeof row.title === 'string' ? row.title : null,
    email: typeof row.email === 'string' ? row.email : null,
    emailed_at: typeof row.emailed_at === 'string' ? row.emailed_at : null,
    viewed_at: typeof row.viewed_at === 'string' ? row.viewed_at : null,
    started_at: typeof row.started_at === 'string' ? row.started_at : null,
    consented_at: typeof row.consented_at === 'string' ? row.consented_at : null,
    signed_at: typeof row.signed_at === 'string' ? row.signed_at : null,
    submission_json: normalizeSlaAgreementCustomerSubmission(
      normalizeJsonRecord(row.submission_json) as Partial<SlaAgreementCustomerSubmission> | null
    ),
    signature_type: row.signature_type === 'typed' ? 'typed' : null,
    signature_text: typeof row.signature_text === 'string' ? row.signature_text : null,
    last_ip_address: typeof row.last_ip_address === 'string' ? row.last_ip_address : null,
    last_user_agent: typeof row.last_user_agent === 'string' ? row.last_user_agent : null,
    revoked_at: typeof row.revoked_at === 'string' ? row.revoked_at : null,
    expired_at: typeof row.expired_at === 'string' ? row.expired_at : null,
    superseded_at: typeof row.superseded_at === 'string' ? row.superseded_at : null,
    created_by: typeof row.created_by === 'string' ? row.created_by : null,
    updated_by: typeof row.updated_by === 'string' ? row.updated_by : null,
    tokens,
  };
}

export function normalizeSlaAgreementSendPackage(row: Record<string, unknown>): SlaAgreementSendPackage {
  const signers = Array.isArray(row.signers)
    ? row.signers.map((signer) => normalizeSlaAgreementSigner(signer as Record<string, unknown>))
    : Array.isArray(row.sla_agreement_signers)
      ? row.sla_agreement_signers.map((signer) => normalizeSlaAgreementSigner(signer as Record<string, unknown>))
      : [];

  return {
    ...(row as unknown as SlaAgreementSendPackage),
    template_key: String(row.template_key || DEFAULT_SLA_TEMPLATE_KEY),
    template_version: String(row.template_version || DEFAULT_SLA_TEMPLATE_VERSION),
    generated_file_path: String(row.generated_file_path || ''),
    draft_payload_json: normalizeSlaDraftPayload((row.draft_payload_json as SlaAgreementDraftPayload | null) || null),
    email_id: typeof row.email_id === 'string' ? row.email_id : null,
    sent_at: typeof row.sent_at === 'string' ? row.sent_at : null,
    expires_at: typeof row.expires_at === 'string' ? row.expires_at : null,
    revoked_at: typeof row.revoked_at === 'string' ? row.revoked_at : null,
    expired_at: typeof row.expired_at === 'string' ? row.expired_at : null,
    superseded_at: typeof row.superseded_at === 'string' ? row.superseded_at : null,
    created_by: typeof row.created_by === 'string' ? row.created_by : null,
    updated_by: typeof row.updated_by === 'string' ? row.updated_by : null,
    signers,
  };
}

export function normalizeSlaAgreementEvent(row: Record<string, unknown>): SlaAgreementEvent {
  return {
    ...(row as unknown as SlaAgreementEvent),
    send_package_id: typeof row.send_package_id === 'string' ? row.send_package_id : null,
    signer_id: typeof row.signer_id === 'string' ? row.signer_id : null,
    token_id: typeof row.token_id === 'string' ? row.token_id : null,
    package_version: typeof row.package_version === 'number' ? row.package_version : null,
    actor_user_id: typeof row.actor_user_id === 'string' ? row.actor_user_id : null,
    actor_label: typeof row.actor_label === 'string' ? row.actor_label : null,
    details_json: normalizeJsonRecord(row.details_json),
  };
}

export function normalizeSlaAgreementRevisionRequest(
  row: Record<string, unknown>
): SlaAgreementRevisionRequest {
  return {
    ...(row as unknown as SlaAgreementRevisionRequest),
    resolved_at: typeof row.resolved_at === 'string' ? row.resolved_at : null,
    resolved_by: typeof row.resolved_by === 'string' ? row.resolved_by : null,
  };
}

export function buildSlaAgreementNumber(existingNumbers: string[]): string {
  const year = new Date().getFullYear();
  const maxSuffix = existingNumbers
    .filter((number) => number.startsWith(`SLA-${year}-`))
    .map((number) => number.match(/SLA-\d{4}-(\d+)/)?.[1] || '0')
    .map((suffix) => Number.parseInt(suffix, 10))
    .reduce((max, current) => (current > max ? current : max), 0);

  return `SLA-${year}-${String(maxSuffix + 1).padStart(3, '0')}`;
}
